/**
 * AI connectors: the consent calls behind the `/connect` page, and the
 * company's settings.
 *
 *   GET  /api/v1/oauth/authorization-requests/{id}          → AuthorizationRequestView
 *   POST /api/v1/oauth/authorization-requests/{id}:approve  → ConsentRedirect
 *   POST /api/v1/oauth/authorization-requests/{id}:deny     → ConsentRedirect
 *   GET  /api/v1/tenants/me/integrations/ai-connectors      → AiConnectorSettings
 *   PUT  /api/v1/tenants/me/integrations/ai-connectors      → AiConnectorSettings
 *   GET  /api/v1/ai-actions/{id}                            → AiActionView
 *   POST /api/v1/ai-actions/{id}:approve | :decline         → AiActionDecision
 *
 * The API answers the consent calls only from the browser that started the
 * request: the binding cookie it set at `/oauth/authorize` travels on the
 * shared client's `credentials: "include"` (`client.ts`). Nothing here may
 * override that. Those calls are caller-scoped (the JWT names the person), so
 * their key lives under `qk.me`; no invalidation after an answer, as the
 * request is gone once answered and a refetch would only fetch the 404.
 *
 * The settings are the account owner's (`tenant:settings:manage`, admin
 * audience) and the PUT takes the whole body, except `allowChanges`: omitted,
 * the server leaves it as stored.
 *
 * An AI action is a change an AI app prepared that touches someone else; the
 * person who asked approves or declines it here. The server binds it to the
 * caller and answers anyone else 404, so its key lives under `qk.me`.
 */
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantQuery, useTenantMutation } from "./factory";
import { useApiClient } from "../react/ApiProvider";
import { qk } from "../query-keys/qk";
import type { ApiError } from "../errors/ApiError";
import type { components } from "../generated/schema";
import type { ProposalView } from "../assistant/generated-events";

export type AuthorizationRequestView = components["schemas"]["AuthorizationRequestView"];
export type ConsentRedirect = components["schemas"]["ConsentRedirect"];
export type ConnectorAnswer = "approve" | "deny";
export type AiConnectorSettings = components["schemas"]["AiConnectorSettings"];
export type UpdateAiConnectorSettingsRequest =
  components["schemas"]["UpdateAiConnectorSettingsRequest"];
/**
 * The contract types `card` as a generic JSON node and describes it as the
 * assistant events schema's ProposalView, which is what it carries.
 */
export type AiActionView = Omit<components["schemas"]["AiActionView"], "card"> & {
  card?: ProposalView | null;
};
export type AiActionDecision = components["schemas"]["AiActionDecision"];
export type AiActionStatus = AiActionView["status"];
export type AiActionResultType = NonNullable<AiActionView["resultType"]>;
/** Why a STALE change could no longer run as prepared: the backend's closed list. */
export type AiActionStaleCode =
  | "MCP_CHANGES_DISABLED"
  | "MCP_CHANGES_NOT_CONSENTED"
  | "ACCESS_DENIED"
  | "MCP_CONNECTORS_DISABLED"
  | "MCP_NOT_ALLOWED"
  | "PLAN_LOCKED"
  | "ASSISTANT_PROPOSAL_STALE";

/** The approve and decline calls, on the encoded spelling the typed paths cannot express. */
interface EncodedColonPost {
  POST: (
    path: string,
    init?: { body: components["schemas"]["ApproveRequest"] },
  ) => Promise<{ data?: AiActionDecision }>;
}

export type AiActionChoice =
  | { id: string; choice: "approve"; proposalHash: string }
  | { id: string; choice: "decline" };

/** The request the signed-in person is asked to approve. */
export function useConnectorRequest(
  id: string,
  { enabled }: { enabled: boolean },
): UseQueryResult<AuthorizationRequestView, ApiError> {
  const client = useApiClient();
  return useTenantQuery<AuthorizationRequestView>(client, {
    queryKey: qk.me.connectorRequest(id),
    enabled,
    // Read once. The request is single-use, and a network blip (the browser
    // fires "online") would otherwise refetch it, racing the answer and
    // showing a just-approved request as expired or already answered.
    refetchOnReconnect: false,
    queryFn: async (c) => {
      const res = await c.GET("/api/v1/oauth/authorization-requests/{id}", {
        params: { path: { id } },
      });
      return res.data!;
    },
  });
}

/**
 * Approve or deny; either way the answer is where to send the browser next.
 * An approval carries `canChangeData`: whether the page showed that the app
 * can make changes. The server stores it only if changes are still allowed.
 */
export function useAnswerConnectorRequest(): UseMutationResult<
  ConsentRedirect,
  ApiError,
  { id: string; answer: ConnectorAnswer; canChangeData?: boolean }
> {
  const client = useApiClient();
  return useTenantMutation<
    { id: string; answer: ConnectorAnswer; canChangeData?: boolean },
    ConsentRedirect
  >(client, {
    mutationFn: async (c, { id, answer, canChangeData }) => {
      const params = { params: { path: { id } } };
      const res =
        answer === "approve"
          ? await c.POST("/api/v1/oauth/authorization-requests/{id}:approve", {
              ...params,
              body: { canChangeData: canChangeData === true },
            })
          : await c.POST("/api/v1/oauth/authorization-requests/{id}:deny", params);
      return res.data!;
    },
  });
}

/** Whether connectors are on, who may connect and what is shared; `available` = the server runs them. */
export function useAiConnectorSettings(
  slug: string,
): UseQueryResult<AiConnectorSettings, ApiError> {
  const client = useApiClient();
  return useTenantQuery<AiConnectorSettings>(client, {
    queryKey: qk.integrations.aiConnectors(slug),
    queryFn: async (c) => {
      const res = await c.GET("/api/v1/tenants/me/integrations/ai-connectors");
      return res.data!;
    },
  });
}

/** Save the whole setting; the answer replaces the cached one. */
export function useUpdateAiConnectorSettings(
  slug: string,
): UseMutationResult<AiConnectorSettings, ApiError, UpdateAiConnectorSettingsRequest> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useTenantMutation<UpdateAiConnectorSettingsRequest, AiConnectorSettings>(client, {
    mutationFn: async (c, body) => {
      const res = await c.PUT("/api/v1/tenants/me/integrations/ai-connectors", { body });
      return res.data!;
    },
    onSuccess: (data) => qc.setQueryData(qk.integrations.aiConnectors(slug), data),
  });
}

/**
 * The change an AI app is waiting on, in any state. 404 = unknown or not the caller's.
 * With `poll`, an EXECUTING change is read again every 2 s: it normally settles within seconds.
 */
export function useAiAction(
  id: string,
  { enabled, poll = false }: { enabled: boolean; poll?: boolean },
): UseQueryResult<AiActionView, ApiError> {
  const client = useApiClient();
  return useTenantQuery<AiActionView>(client, {
    queryKey: qk.me.aiAction(id),
    enabled,
    refetchInterval: (q) => (poll && q.state.data?.status === "EXECUTING" ? 2_000 : false),
    // The caller's give-up timer keeps running in a hidden tab; so must the reads,
    // or a change that settled meanwhile would still read as unconfirmed.
    refetchIntervalInBackground: true,
    queryFn: async (c) => {
      const res = await c.GET("/api/v1/ai-actions/{id}", { params: { path: { id } } });
      // `card` arrives as the ProposalView the contract describes (see AiActionView).
      return res.data as AiActionView;
    },
  });
}

/**
 * Approve (echoing the card's `proposalHash` verbatim) or decline. Whatever the
 * answer, success or a 409, the action is read again: its state is the server's.
 */
export function useDecideAiAction(): UseMutationResult<AiActionDecision, ApiError, AiActionChoice> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useTenantMutation<AiActionChoice, AiActionDecision>(client, {
    mutationFn: async (c, v) => {
      // Percent-encoded colon, as the assistant's approve: staging refuses a
      // literal `:approve` in front of the backend; the backend routes both.
      const path = `/api/v1/ai-actions/${encodeURIComponent(v.id)}%3A${v.choice}`;
      const res = await (c as unknown as EncodedColonPost).POST(
        path,
        v.choice === "approve" ? { body: { proposalHash: v.proposalHash } } : undefined,
      );
      return res.data!;
    },
    onSettled: (_data, _error, v) => qc.invalidateQueries({ queryKey: qk.me.aiAction(v.id) }),
  });
}
